import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { StripeSection } from "@/components/billing/StripeSection";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  CreditCard,
  Calendar,
  TrendingUp,
  Target,
  Clock,
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  MessageSquare,
  UserPlus,
  BarChart3,
  FileText,
  Euro,
  Users,
  Ticket,
  Zap,
  Plus,
  Minus,
  History,
  Coins,
  HardHat,
  Smartphone,
  Layers,
  Shield,
  Key,
  XCircle,
  ShieldAlert,
  ShieldCheck,
  Loader2,
  RefreshCw,
  LogIn,
  Trash2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { PLAN_LABELS, ALL_PLANS } from "@/lib/planLabels";

// ─── Config ───────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  TRIAL: { label: "Essai", variant: "outline" },
  ACTIVE: { label: "Actif", variant: "default" },
  PAST_DUE: { label: "Retard paiement", variant: "destructive" },
  SUSPENDED: { label: "Suspendu", variant: "secondary" },
  CANCELLED: { label: "Annulé", variant: "destructive" },
};

const TENDER_DECISION_LABELS: Record<string, string> = {
  GO: "GO",
  NO_GO: "NO GO",
  PENDING: "En attente",
};

const TENDER_OUTCOME_LABELS: Record<string, string> = {
  WON: "Gagné",
  LOST: "Perdu",
  IN_PROGRESS: "En cours",
};

const TICKET_STATUS_CONFIG: Record<string, { label: string; isOpen: boolean }> = {
  NEW: { label: "Nouveau", isOpen: true },
  IN_PROGRESS: { label: "En cours", isOpen: true },
  WAITING_CLIENT: { label: "Attente client", isOpen: true },
  ESCALATED: { label: "Escaladé", isOpen: true },
  RESOLVED: { label: "Résolu", isOpen: false },
  CLOSED: { label: "Fermé", isOpen: false },
};

// ─── Feature config ───────────────────────────────────────────────────────────

// ⚠️ Doit couvrir TOUTES les clés de `ALL_FEATURE_KEYS`
// (apps/server/src/routers/companyFeatures.ts). Toute clé renvoyée par le
// backend mais absente de ces catégories est rendue automatiquement dans le
// groupe « Autres » (filet anti-régression), mais on garde la liste explicite
// à jour pour le libellé + le bon regroupement. AOP-8995 / retour fondateur :
// l'ancien set ne montrait que 24/52 fonctionnalités SAAS + mobile.
const FEATURE_CATEGORIES: { label: string; keys: string[] }[] = [
  {
    label: "Finances",
    keys: [
      "quotes",
      "invoices",
      "recurring_invoices",
      "purchase_invoices",
      "purchase_orders",
      "retenues",
      "situations",
      "expenses",
      "invoice_risk_score",
      "quote_profitability_score",
      "retention_guarantee",
      "sepa_mandate",
      "dsn_export",
    ],
  },
  {
    label: "Chantier & Terrain",
    keys: [
      "interventions",
      "planning",
      "weather-adaptive-planning",
      "timesheets",
      "reports",
      "documents",
      "checklists",
      "site_journal",
      "schedule_slip_radar",
    ],
  },
  {
    label: "HSE & Sécurité",
    keys: ["incidents", "danger_reports"],
  },
  {
    label: "Matériel, Achats & Sous-traitance",
    keys: [
      "materials",
      "equipment",
      "fuel_tracking",
      "subcontractors",
      "subcontractor_compliance",
      "certifications",
      "frame_contracts",
      "avenant_radar",
    ],
  },
  {
    label: "Collaboration",
    keys: [
      "messaging",
      "messaging_templates",
      "reminders",
      "client_portal",
      "client_portal_advanced",
      "custom_smtp",
    ],
  },
  {
    label: "IA & Analytics",
    keys: [
      "ai_assistant",
      "service_catalog",
      "dormant_pipeline",
      "peer_benchmark",
      "gpa_tracker",
      "skills_gap_analysis",
    ],
  },
  {
    label: "Intégrations & API",
    keys: [
      "outgoing_webhooks",
      "webhooks_outbound",
      "api_keys",
      "api_tokens",
      "bank_sync",
      "automation_engine",
    ],
  },
  {
    label: "Crédits & Permissions",
    keys: ["ao_credits_packs", "perm_v1"],
  },
];

const FEATURE_LABELS: Record<string, string> = {
  quotes: "Devis",
  invoices: "Factures",
  recurring_invoices: "Factures récurrentes",
  purchase_invoices: "Factures achat",
  purchase_orders: "Commandes",
  retenues: "Retenues de garantie",
  situations: "Situations de travaux",
  expenses: "Dépenses",
  invoice_risk_score: "Score de risque facture",
  quote_profitability_score: "Score de rentabilité devis",
  retention_guarantee: "Garantie de retenue",
  sepa_mandate: "Mandat SEPA",
  dsn_export: "Export DSN",
  interventions: "Interventions",
  planning: "Planning",
  "weather-adaptive-planning": "Planning météo-adaptatif",
  timesheets: "Feuilles de temps",
  reports: "Rapports",
  documents: "Documents",
  checklists: "Check-lists",
  site_journal: "Journal de chantier",
  schedule_slip_radar: "Radar dérive planning",
  incidents: "Incidents",
  danger_reports: "Signalements de danger",
  materials: "Matériaux",
  equipment: "Matériel / Équipements",
  fuel_tracking: "Suivi carburant",
  subcontractors: "Sous-traitants",
  subcontractor_compliance: "Conformité sous-traitants",
  certifications: "Certifications",
  frame_contracts: "Contrats-cadres",
  avenant_radar: "Radar avenants",
  messaging: "Messagerie",
  messaging_templates: "Modèles de messages",
  reminders: "Relances",
  client_portal: "Portail client",
  client_portal_advanced: "Portail client avancé",
  custom_smtp: "SMTP personnalisé",
  ai_assistant: "Assistant IA",
  service_catalog: "Catalogue service",
  dormant_pipeline: "Pipeline dormant",
  peer_benchmark: "Benchmark sectoriel",
  gpa_tracker: "Suivi GPA",
  skills_gap_analysis: "Analyse des écarts de compétences",
  outgoing_webhooks: "Webhooks sortants",
  webhooks_outbound: "Webhooks sortants (avancé)",
  api_keys: "Clés API",
  api_tokens: "Jetons API",
  bank_sync: "Synchronisation bancaire",
  automation_engine: "Moteur d'automatisation",
  ao_credits_packs: "Packs crédits AO",
  perm_v1: "Permissions granulaires",
};

// ─── Credit config ───────────────────────────────────────────────────────────

const CREDIT_REASON_OPTIONS = [
  { value: "commercial_gift", label: "Cadeau commercial" },
  { value: "compensation", label: "Compensation" },
  { value: "error_correction", label: "Correction d'erreur" },
  { value: "other", label: "Autre" },
];

const TX_TYPE_CONFIG: Record<string, { label: string; className: string }> = {
  PURCHASE: { label: "Achat", className: "bg-primary/10 text-primary border-primary/20" },
  UNLOCK: { label: "Déverrouillage", className: "bg-destructive/10 text-destructive border-destructive/20" },
  REFUND: { label: "Remboursement", className: "bg-success/10 text-success border-success/20" },
  BONUS_WELCOME: { label: "Bonus bienvenue", className: "bg-success/10 text-success border-success/20" },
  BONUS_REFERRAL: { label: "Bonus parrainage", className: "bg-success/10 text-success border-success/20" },
  MANUAL_ADD: { label: "Ajout manuel", className: "bg-success/10 text-success border-success/20" },
  MANUAL_REMOVE: { label: "Retrait manuel", className: "bg-warning/10 text-warning border-warning/20" },
};

// ─── Features Tab ─────────────────────────────────────────────────────────────

function FeaturesTab({ companyId, canAct }: { companyId: string; canAct: boolean }) {
  const [confirmDialog, setConfirmDialog] = useState<{ featureKey: string; enabled: boolean } | null>(null);
  const [reason, setReason] = useState("");

  const { data: featuresData, isLoading, refetch } = trpc.admin.companyFeatures.list.useQuery(
    { companyId },
    { enabled: !!companyId }
  );
  const { data: historyData, isLoading: historyLoading, refetch: refetchHistory } =
    trpc.admin.companyFeatures.history.useQuery(
      { companyId, limit: 30 },
      { enabled: !!companyId }
    );

  const setMutation = trpc.admin.companyFeatures.set.useMutation({
    onSuccess: (data: { featureKey: string; enabled: boolean }) => {
      const label = FEATURE_LABELS[data.featureKey] ?? data.featureKey;
      toast.success(`${label} ${data.enabled ? "activé" : "désactivé"}`);
      setConfirmDialog(null);
      setReason("");
      refetch();
      refetchHistory();
    },
    onError: () => toast.error("Erreur lors du changement"),
  });

  function handleToggle(featureKey: string, currentEnabled: boolean) {
    if (!canAct) return;
    const newEnabled = !currentEnabled;
    if (!newEnabled) {
      // Confirm before disabling
      setConfirmDialog({ featureKey, enabled: false });
    } else {
      setMutation.mutate({ companyId, featureKey: featureKey as any, enabled: true });
    }
  }

  function handleConfirmDisable() {
    if (!confirmDialog) return;
    setMutation.mutate({
      companyId,
      featureKey: confirmDialog.featureKey as any,
      enabled: false,
      reason: reason.trim() || undefined,
    });
  }

  if (isLoading) {
    return <div className="py-8 text-center text-muted-foreground text-sm">Chargement des fonctionnalités…</div>;
  }

  const features = featuresData?.features ?? [];
  const plan = featuresData?.plan ?? "DECOUVERTE";
  const featureMap = new Map(features.map((f: any) => [f.featureKey, f]));

  // Filet anti-régression : toute clé renvoyée par le backend qui n'est listée
  // dans aucune catégorie ci-dessus est regroupée dans « Autres ». Garantit que
  // l'ajout d'une feature côté serveur reste pilotable depuis l'OPS même si la
  // catégorisation n'a pas encore été mise à jour.
  const categorizedKeys = new Set(FEATURE_CATEGORIES.flatMap((c) => c.keys));
  const uncategorizedKeys = features
    .map((f: any) => f.featureKey)
    .filter((k: string) => !categorizedKeys.has(k));
  const renderedCategories =
    uncategorizedKeys.length > 0
      ? [...FEATURE_CATEGORIES, { label: "Autres", keys: uncategorizedKeys }]
      : FEATURE_CATEGORIES;

  return (
    <div className="space-y-8">
      {/* Feature groups */}
      {renderedCategories.map((cat) => (
        <div key={cat.label}>
          <h3 className="font-semibold text-sm mb-3 text-muted-foreground uppercase tracking-wide">
            {cat.label}
          </h3>
          <Card>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {cat.keys.map((key) => {
                  const feat = featureMap.get(key) as any;
                  if (!feat) return null;
                  const label = FEATURE_LABELS[key] ?? key;
                  const isOverride = feat.source === "override";
                  return (
                    <div key={key} className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="min-w-[180px]">
                          <div className="text-sm font-medium">{label}</div>
                        </div>
                        <Badge
                          variant="outline"
                          className={`text-xs ${
                            isOverride
                              ? "bg-warning/10 border-warning/30 text-warning"
                              : feat.enabled
                                ? "bg-muted/50 text-muted-foreground"
                                : "bg-destructive/10 text-destructive border-destructive/20"
                          }`}
                        >
                          {isOverride
                            ? "Override manuel"
                            : feat.enabled
                              ? `Inclus Plan ${plan}`
                              : `Non inclus Plan ${plan}`}
                        </Badge>
                      </div>
                      <Switch
                        checked={feat.enabled}
                        onCheckedChange={() => handleToggle(key, feat.enabled)}
                        disabled={!canAct || setMutation.isPending}
                        aria-label={`Activer/désactiver ${label}`}
                      />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      ))}

      {/* History */}
      <div>
        <h3 className="font-semibold text-sm mb-3 text-muted-foreground uppercase tracking-wide">
          Historique des changements
        </h3>
        {historyLoading ? (
          <div className="text-center text-muted-foreground text-sm py-4">Chargement…</div>
        ) : !historyData?.length ? (
          <p className="text-sm text-muted-foreground">Aucun changement enregistré</p>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fonctionnalité</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Raison</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(historyData as any[]).map((log: any) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-sm font-medium">
                        {FEATURE_LABELS[log.featureKey] ?? log.featureKey}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={`text-xs ${
                            log.action === "enabled"
                              ? "bg-success/10 text-success border-success/20"
                              : "bg-destructive/10 text-destructive border-destructive/20"
                          }`}
                        >
                          {log.action === "enabled" ? "Activé" : "Désactivé"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {log.reason ?? "—"}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(log.createdAt).toLocaleDateString("fr-FR", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Confirm disable dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={() => { setConfirmDialog(null); setReason(""); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Désactiver la fonctionnalité</DialogTitle>
            <DialogDescription>
              Désactiver{" "}
              <strong>
                {confirmDialog ? (FEATURE_LABELS[confirmDialog.featureKey] ?? confirmDialog.featureKey) : ""}
              </strong>{" "}
              pour cette entreprise ?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm text-muted-foreground">Raison (optionnel)</label>
            <Input
              placeholder="Ex: plan dégradé, essai terminé..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmDialog(null); setReason(""); }}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDisable}
              disabled={setMutation.isPending}
            >
              {setMutation.isPending ? "Désactivation…" : "Désactiver"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Access & Users Tab ─────────────────────────────────────────────────────

const APP_COLORS: Record<string, string> = {
  SAAS: "bg-blue-500/10 text-blue-600 border-blue-200",
  TRAVAUXBTP: "bg-orange-500/10 text-orange-600 border-orange-200",
  MOBILE: "bg-purple-500/10 text-purple-600 border-purple-200",
  ARCHITECT: "bg-teal-500/10 text-teal-600 border-teal-200",
};

const APP_LABELS: Record<string, string> = {
  SAAS: "SaaS",
  TRAVAUXBTP: "TravauxBTP",
  MOBILE: "Mobile",
  ARCHITECT: "Architect",
};

const ROLE_LABELS: Record<string, string> = {
  OWNER: "Propriétaire",
  PRO: "Pro",
  PARTICULIER: "Particulier",
  DIRIGEANT: "Dirigeant",
  EMPLOYE: "Employé",
  FREEMIUM: "Freemium",
  ARCHITECTE: "Architecte",
};

function AccessTab({ companyId, canAct }: { companyId: string; canAct: boolean }) {
  const { data, isLoading, refetch } = trpc.admin.companies.getCompanyAccess.useQuery(
    { companyId },
    { enabled: !!companyId }
  );

  const [grantDialog, setGrantDialog] = useState(false);
  const [grantApp, setGrantApp] = useState("SAAS");
  const [grantRole, setGrantRole] = useState("PRO");
  const [grantUserId, setGrantUserId] = useState("");

  const [revokeDialog, setRevokeDialog] = useState<{ accessId: string; userName: string; app: string } | null>(null);
  const [revokeReason, setRevokeReason] = useState("");

  const [changeRoleDialog, setChangeRoleDialog] = useState<{
    userId: string;
    app: string;
    currentRole: string;
    userName: string;
  } | null>(null);
  const [newRole, setNewRole] = useState("");

  const grantMutation = trpc.admin.companies.grantAccess.useMutation({
    onSuccess: () => {
      toast.success("Accès accordé");
      setGrantDialog(false);
      setGrantUserId("");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const revokeMutation = trpc.admin.companies.revokeAccess.useMutation({
    onSuccess: () => {
      toast.success("Accès révoqué");
      setRevokeDialog(null);
      setRevokeReason("");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const changeRoleMutation = trpc.admin.companies.changeAccessRole.useMutation({
    onSuccess: () => {
      toast.success("Rôle modifié");
      setChangeRoleDialog(null);
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const isActing = grantMutation.isPending || revokeMutation.isPending || changeRoleMutation.isPending;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
        Chargement…
      </div>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground text-sm">
          Impossible de charger les accès
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Mobile seats counter */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Smartphone className="h-4 w-4" />
          <span>
            Sièges mobile actifs : <span className="font-medium text-foreground">{data.mobileSeatsCount}</span>
          </span>
        </div>
        {canAct && (
          <Button size="sm" variant="outline" onClick={() => setGrantDialog(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            Ajouter un accès
          </Button>
        )}
      </div>

      {/* Users table */}
      {data.users.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground text-sm">
            Aucun utilisateur avec des accès pour cette entreprise
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Utilisateur</TableHead>
                  <TableHead>Accès</TableHead>
                  <TableHead className="text-center">Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.users.map((user) => (
                  <TableRow key={user.userId}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">
                          {[user.firstName, user.lastName].filter(Boolean).join(" ") || "—"}
                        </p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {user.accesses.map((access) => (
                          <Badge
                            key={access.id}
                            className={`text-xs ${
                              access.isActive
                                ? APP_COLORS[access.app] || ""
                                : "bg-muted text-muted-foreground line-through"
                            }`}
                          >
                            {APP_LABELS[access.app] || access.app} {ROLE_LABELS[access.role] || access.role}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {user.accesses.some((a) => a.isActive) ? (
                        <Badge variant="default" className="text-xs">Actif</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">Révoqué</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {canAct && (
                        <div className="flex items-center justify-end gap-1">
                          {user.accesses
                            .filter((a) => a.isActive)
                            .map((access) => (
                              <div key={access.id} className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-xs"
                                  disabled={isActing}
                                  onClick={() => {
                                    setChangeRoleDialog({
                                      userId: user.userId,
                                      app: access.app,
                                      currentRole: access.role,
                                      userName: [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email,
                                    });
                                    setNewRole(access.role);
                                  }}
                                >
                                  <Shield className="h-3 w-3 mr-1" />
                                  Rôle
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                                  disabled={isActing}
                                  onClick={() =>
                                    setRevokeDialog({
                                      accessId: access.id,
                                      userName: [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email,
                                      app: APP_LABELS[access.app] || access.app,
                                    })
                                  }
                                >
                                  <XCircle className="h-3 w-3" />
                                </Button>
                              </div>
                            ))}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Grant access dialog */}
      <Dialog open={grantDialog} onOpenChange={setGrantDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ajouter un accès</DialogTitle>
            <DialogDescription>
              Accorder un accès application à un utilisateur de cette entreprise.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium">User ID</label>
              <Input
                placeholder="ID utilisateur"
                value={grantUserId}
                onChange={(e) => setGrantUserId(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-medium">Application</label>
              <Select value={grantApp} onValueChange={setGrantApp}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="SAAS">SaaS</SelectItem>
                  <SelectItem value="TRAVAUXBTP">TravauxBTP</SelectItem>
                  <SelectItem value="MOBILE">Mobile</SelectItem>
                  <SelectItem value="ARCHITECT">Architect</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium">Rôle</label>
              <Select value={grantRole} onValueChange={setGrantRole}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="OWNER">Propriétaire</SelectItem>
                  <SelectItem value="PRO">Pro</SelectItem>
                  <SelectItem value="DIRIGEANT">Dirigeant</SelectItem>
                  <SelectItem value="EMPLOYE">Employé</SelectItem>
                  <SelectItem value="FREEMIUM">Freemium</SelectItem>
                  <SelectItem value="ARCHITECTE">Architecte</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrantDialog(false)}>
              Annuler
            </Button>
            <Button
              disabled={!grantUserId.trim() || isActing}
              onClick={() =>
                grantMutation.mutate({
                  userId: grantUserId.trim(),
                  app: grantApp as any,
                  role: grantRole as any,
                  companyId,
                })
              }
            >
              {grantMutation.isPending ? "Ajout…" : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke access dialog */}
      <Dialog open={!!revokeDialog} onOpenChange={(open) => !open && setRevokeDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Révoquer l'accès</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Révoquer l'accès <span className="font-medium text-foreground">{revokeDialog?.app}</span>{" "}
            de <span className="font-medium text-foreground">{revokeDialog?.userName}</span> ?
          </p>
          <div>
            <label className="text-xs font-medium">Raison (optionnel)</label>
            <Input
              placeholder="Motif de révocation..."
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeDialog(null)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              disabled={isActing}
              onClick={() => {
                if (!revokeDialog) return;
                revokeMutation.mutate({
                  accessId: revokeDialog.accessId,
                  reason: revokeReason || undefined,
                });
              }}
            >
              {revokeMutation.isPending ? "Révocation…" : "Révoquer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change role dialog */}
      <Dialog open={!!changeRoleDialog} onOpenChange={(open) => !open && setChangeRoleDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Changer le rôle</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Modifier le rôle {APP_LABELS[changeRoleDialog?.app || ""] || changeRoleDialog?.app}{" "}
            de <span className="font-medium text-foreground">{changeRoleDialog?.userName}</span>.
          </p>
          <div>
            <label className="text-xs font-medium">Nouveau rôle</label>
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="OWNER">Propriétaire</SelectItem>
                <SelectItem value="PRO">Pro</SelectItem>
                <SelectItem value="DIRIGEANT">Dirigeant</SelectItem>
                <SelectItem value="EMPLOYE">Employé</SelectItem>
                <SelectItem value="FREEMIUM">Freemium</SelectItem>
                <SelectItem value="ARCHITECTE">Architecte</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeRoleDialog(null)}>
              Annuler
            </Button>
            <Button
              disabled={isActing || newRole === changeRoleDialog?.currentRole}
              onClick={() => {
                if (!changeRoleDialog) return;
                changeRoleMutation.mutate({
                  userId: changeRoleDialog.userId,
                  companyId,
                  app: changeRoleDialog.app as any,
                  newRole: newRole as any,
                });
              }}
            >
              {changeRoleMutation.isPending ? "Modification…" : "Modifier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Credits Tab ─────────────────────────────────────────────────────────────

const AUTO_TOPUP_PACK_LABELS: Record<string, string> = {
  unit: "Pack Découverte (5 crédits)",
  starter: "Pack Starter (10 crédits)",
  pro: "Pack Pro (25 crédits)",
  business: "Pack Business (50 crédits)",
  expert: "Pack Expert (100 crédits)",
};

function CreditsTab({
  companyId,
  leadCredits,
  canAct,
  autoTopupEnabled,
  autoTopupThreshold,
  autoTopupPackId,
}: {
  companyId: string;
  leadCredits: number;
  canAct: boolean;
  autoTopupEnabled: boolean;
  autoTopupThreshold: number | null;
  autoTopupPackId: string | null;
}) {
  const [addDialog, setAddDialog] = useState(false);
  const [removeDialog, setRemoveDialog] = useState(false);
  const [amount, setAmount] = useState("");
  const [creditReason, setCreditReason] = useState("");
  const [customReason, setCustomReason] = useState("");

  const utils = trpc.useUtils();

  const { data: txData, isLoading: txLoading, refetch: refetchTx } =
    trpc.admin.travauxbtp.getCompanyCreditHistory.useQuery(
      { companyId, limit: 50 },
      { enabled: !!companyId }
    );

  const addMutation = trpc.admin.travauxbtp.addCredits.useMutation({
    onSuccess: () => {
      toast.success(`${amount} crédits ajoutés`);
      resetForm();
      setAddDialog(false);
      refetchTx();
      utils.admin.companies.getById.invalidate({ id: companyId });
    },
    onError: () => toast.error("Erreur lors de l'ajout de crédits"),
  });

  const removeMutation = trpc.admin.travauxbtp.removeCredits.useMutation({
    onSuccess: () => {
      toast.success(`${amount} crédits retirés`);
      resetForm();
      setRemoveDialog(false);
      refetchTx();
      utils.admin.companies.getById.invalidate({ id: companyId });
    },
    onError: () => toast.error("Erreur lors du retrait de crédits"),
  });

  function resetForm() {
    setAmount("");
    setCreditReason("");
    setCustomReason("");
  }

  function getReasonLabel() {
    if (creditReason === "other") return customReason.trim();
    return CREDIT_REASON_OPTIONS.find((r) => r.value === creditReason)?.label ?? creditReason;
  }

  function handleAdd() {
    const qty = parseInt(amount, 10);
    if (!qty || qty <= 0 || !creditReason) return;
    addMutation.mutate({ companyId, amount: qty, reason: getReasonLabel() });
  }

  function handleRemove() {
    const qty = parseInt(amount, 10);
    if (!qty || qty <= 0 || !creditReason) return;
    if (qty > leadCredits) {
      toast.error("Impossible de retirer plus de crédits que le solde actuel");
      return;
    }
    removeMutation.mutate({ companyId, amount: qty, reason: getReasonLabel() });
  }

  const transactions: any[] = txData?.items ?? [];
  const parsedAmount = parseInt(amount, 10);
  const isFormValid = parsedAmount > 0 && !!creditReason && (creditReason !== "other" || customReason.trim().length > 0);

  return (
    <div className="space-y-6">
      {/* Balance + actions */}
      <div className="flex items-start gap-4">
        <Card className="flex-1">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs text-muted-foreground mb-1">Solde actuel</div>
                <div className="text-3xl font-bold">{leadCredits}</div>
                <div className="text-xs text-muted-foreground mt-0.5">crédits disponibles</div>
              </div>
              <div
                aria-hidden="true"
                className={`p-2 rounded-lg ${
                  leadCredits <= 2
                    ? "bg-destructive/10 text-destructive"
                    : leadCredits <= 5
                      ? "bg-warning/10 text-warning"
                      : "bg-success/10 text-success"
                }`}
              >
                <Coins className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>

        {canAct && (
          <div className="flex flex-col gap-2">
            <Button size="sm" onClick={() => setAddDialog(true)}>
              <Plus aria-hidden="true" className="h-3.5 w-3.5 mr-1" />
              Ajouter des crédits
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setRemoveDialog(true)}
              disabled={leadCredits <= 0}
            >
              <Minus aria-hidden="true" className="h-3.5 w-3.5 mr-1" />
              Retirer des crédits
            </Button>
          </div>
        )}
      </div>

      {/* Auto-topup status */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <RefreshCw aria-hidden="true" className="h-4 w-4" />
              Rechargement automatique
            </h3>
            <Badge variant={autoTopupEnabled ? "default" : "secondary"}>
              {autoTopupEnabled ? "Actif" : "Inactif"}
            </Badge>
          </div>
          {autoTopupEnabled ? (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground text-xs mb-0.5">Seuil de déclenchement</div>
                <div className="font-medium">{autoTopupThreshold ?? 3} crédits restants</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs mb-0.5">Pack configuré</div>
                <div className="font-medium">
                  {autoTopupPackId ? (AUTO_TOPUP_PACK_LABELS[autoTopupPackId] ?? autoTopupPackId) : "—"}
                </div>
              </div>
              {(() => {
                const lastAutoTopup = transactions.find((tx: any) =>
                  typeof tx.label === "string" && tx.label.startsWith("Rechargement auto")
                );
                if (!lastAutoTopup) return null;
                return (
                  <div className="col-span-2">
                    <div className="text-muted-foreground text-xs mb-0.5">Dernier rechargement auto</div>
                    <div className="font-medium">
                      {new Date(lastAutoTopup.createdAt).toLocaleDateString("fr-FR", {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Le pro n'a pas activé le rechargement automatique de crédits.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Transaction history */}
      <div>
        <h3 className="font-semibold text-sm mb-3 text-muted-foreground uppercase tracking-wide flex items-center gap-2">
          <History aria-hidden="true" className="h-4 w-4" />
          Historique des transactions
        </h3>
        {txLoading ? (
          <div className="text-center text-muted-foreground text-sm py-4">Chargement…</div>
        ) : transactions.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center text-muted-foreground text-sm">
              Aucune transaction enregistrée
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Montant</TableHead>
                    <TableHead>Solde après</TableHead>
                    <TableHead>Motif</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((tx: any) => {
                    const cfg = TX_TYPE_CONFIG[tx.type] ?? {
                      label: tx.type,
                      className: "bg-muted text-muted-foreground",
                    };
                    const isPositive = tx.amount > 0;
                    return (
                      <TableRow key={tx.id}>
                        <TableCell className="text-xs text-muted-foreground">
                          {new Date(tx.createdAt).toLocaleDateString("fr-FR", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-xs ${cfg.className}`}>
                            {cfg.label}
                          </Badge>
                        </TableCell>
                        <TableCell
                          className={`text-sm font-medium ${isPositive ? "text-success" : "text-destructive"}`}
                        >
                          {isPositive ? "+" : ""}
                          {tx.amount}
                        </TableCell>
                        <TableCell className="text-sm">{tx.balance}</TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                          {tx.label}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add credits dialog */}
      <Dialog
        open={addDialog}
        onOpenChange={(open) => {
          if (!open) {
            resetForm();
            setAddDialog(false);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ajouter des crédits</DialogTitle>
            <DialogDescription>
              Ajoutez manuellement des crédits au solde de cette entreprise.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Nombre de crédits</label>
              <Input
                type="number"
                min="1"
                placeholder="Ex: 10"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Motif</label>
              <Select value={creditReason} onValueChange={setCreditReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Choisir un motif" />
                </SelectTrigger>
                <SelectContent>
                  {CREDIT_REASON_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {creditReason === "other" && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Précisez le motif</label>
                <Input
                  placeholder="Motif personnalisé…"
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                resetForm();
                setAddDialog(false);
              }}
            >
              Annuler
            </Button>
            <Button onClick={handleAdd} disabled={!isFormValid || addMutation.isPending}>
              {addMutation.isPending ? "Ajout…" : `Ajouter${parsedAmount > 0 ? ` ${parsedAmount} crédits` : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove credits dialog */}
      <Dialog
        open={removeDialog}
        onOpenChange={(open) => {
          if (!open) {
            resetForm();
            setRemoveDialog(false);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Retirer des crédits</DialogTitle>
            <DialogDescription>
              Retirez manuellement des crédits du solde. Solde actuel : {leadCredits} crédits.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Nombre de crédits à retirer</label>
              <Input
                type="number"
                min="1"
                max={leadCredits}
                placeholder="Ex: 5"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              {parsedAmount > leadCredits && (
                <p className="text-xs text-destructive">
                  Impossible de retirer plus que le solde actuel ({leadCredits})
                </p>
              )}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Motif</label>
              <Select value={creditReason} onValueChange={setCreditReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Choisir un motif" />
                </SelectTrigger>
                <SelectContent>
                  {CREDIT_REASON_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {creditReason === "other" && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Précisez le motif</label>
                <Input
                  placeholder="Motif personnalisé…"
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                resetForm();
                setRemoveDialog(false);
              }}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleRemove}
              disabled={!isFormValid || removeMutation.isPending || parsedAmount > leadCredits}
            >
              {removeMutation.isPending ? "Retrait…" : `Retirer${parsedAmount > 0 ? ` ${parsedAmount} crédits` : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Billing Tab ─────────────────────────────────────────────────────────────

const PLAN_OPTIONS = ALL_PLANS.map((p) => ({ value: p, label: PLAN_LABELS[p] }));

const STATUS_OPTIONS = [
  { value: "TRIAL", label: "Essai" },
  { value: "ACTIVE", label: "Actif" },
  { value: "SUSPENDED", label: "Suspendu" },
  { value: "CANCELLED", label: "Annulé" },
];

function BillingTab({
  company,
  billing,
  statusCfg,
  canAct,
  userRole,
}: {
  company: any;
  billing: any;
  statusCfg: { label: string; variant: "default" | "secondary" | "destructive" | "outline" };
  canAct: boolean;
  userRole: string | undefined;
}) {
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [targetPlan, setTargetPlan] = useState(company.plan ?? "DECOUVERTE");
  const [targetStatus, setTargetStatus] = useState(company.status ?? "TRIAL");
  const [planReason, setPlanReason] = useState("");
  const [targetExpiresAt, setTargetExpiresAt] = useState(
    company.planExpiresAt ? new Date(company.planExpiresAt).toISOString().slice(0, 10) : ""
  );
  const [targetIncludedSeats, setTargetIncludedSeats] = useState(String(company.includedSeats ?? 1));
  const [targetAdditionalSeats, setTargetAdditionalSeats] = useState(String(company.additionalSeats ?? 0));
  const [targetPricePerSeat, setTargetPricePerSeat] = useState(
    company.pricePerSeat != null ? String(company.pricePerSeat) : ""
  );

  const utils = trpc.useUtils();
  const isSuperAdmin = userRole === "SUPER_ADMIN";
  const hasStripeId = !!company.stripeCustomerId;

  const { data: invoicesData, isLoading: invoicesLoading } =
    trpc.admin.stripe.getCustomerInvoices.useQuery(
      { companyId: company.id },
      { enabled: hasStripeId }
    );
  const { data: failedData, isLoading: failedLoading } =
    trpc.admin.stripe.getFailedPayments.useQuery(
      { companyId: company.id },
      { enabled: hasStripeId }
    );
  const { data: methodsData, isLoading: methodsLoading } =
    trpc.admin.stripe.getPaymentMethods.useQuery(
      { companyId: company.id },
      { enabled: hasStripeId }
    );
  const { data: upcomingData, isLoading: upcomingLoading } =
    trpc.admin.stripe.getUpcomingInvoice.useQuery(
      { companyId: company.id },
      { enabled: hasStripeId }
    );
  const { data: subData, isLoading: subLoading } =
    trpc.admin.stripe.getSubscriptionDetails.useQuery(
      { companyId: company.id },
      { enabled: hasStripeId }
    );

  const stripeLoading =
    invoicesLoading || failedLoading || methodsLoading || upcomingLoading || subLoading;

  const invalidateStripe = () => {
    utils.admin.stripe.getCustomerInvoices.invalidate({ companyId: company.id });
    utils.admin.stripe.getFailedPayments.invalidate({ companyId: company.id });
    utils.admin.stripe.getPaymentMethods.invalidate({ companyId: company.id });
    utils.admin.stripe.getUpcomingInvoice.invalidate({ companyId: company.id });
    utils.admin.stripe.getSubscriptionDetails.invalidate({ companyId: company.id });
  };

  const retryMutation = trpc.admin.stripe.retryInvoice.useMutation({
    onSuccess: () => {
      toast.success("Paiement relancé avec succès");
      invalidateStripe();
    },
    onError: (err) => toast.error(err.message || "Erreur lors de la relance"),
  });

  const cancelSubMutation = trpc.admin.stripe.cancelSubscription.useMutation({
    onSuccess: () => {
      toast.success("Abonnement annulé (fin de période)");
      invalidateStripe();
    },
    onError: (err) => toast.error(err.message || "Erreur lors de l'annulation"),
  });

  const refundMutation = trpc.admin.stripe.refundInvoice.useMutation({
    onSuccess: (data) => {
      toast.success(`Remboursement effectué (${data.refundId})`);
      invalidateStripe();
    },
    onError: (err) => toast.error(err.message || "Erreur lors du remboursement"),
  });

  const updateMutation = trpc.admin.companies.update.useMutation({
    onSuccess: () => {
      toast.success("Plan mis à jour avec succès");
      setShowPlanDialog(false);
      setPlanReason("");
      utils.admin.companies.getById.invalidate({ id: company.id });
    },
    onError: (err) => toast.error(err.message || "Erreur lors de la mise à jour"),
  });

  function handlePlanChange() {
    if (planReason.trim().length < 20) return;
    const payload: Record<string, any> = {
      id: company.id,
      plan: targetPlan,
      status: targetStatus,
    };
    if (targetExpiresAt) payload.planExpiresAt = new Date(targetExpiresAt).toISOString();
    const seats = parseInt(targetIncludedSeats, 10);
    if (!isNaN(seats)) payload.includedSeats = seats;
    const addSeats = parseInt(targetAdditionalSeats, 10);
    if (!isNaN(addSeats)) payload.additionalSeats = addSeats;
    const pps = parseFloat(targetPricePerSeat);
    if (!isNaN(pps)) payload.pricePerSeat = pps;
    if (planReason.trim()) payload.reason = planReason.trim();
    updateMutation.mutate(payload as any);
  }

  function openPlanDialog() {
    setTargetPlan(company.plan ?? "DECOUVERTE");
    setTargetStatus(company.status ?? "TRIAL");
    setPlanReason("");
    setTargetExpiresAt(
      company.planExpiresAt ? new Date(company.planExpiresAt).toISOString().slice(0, 10) : ""
    );
    setTargetIncludedSeats(String(company.includedSeats ?? 1));
    setTargetAdditionalSeats(String(company.additionalSeats ?? 0));
    setTargetPricePerSeat(company.pricePerSeat != null ? String(company.pricePerSeat) : "");
    setShowPlanDialog(true);
  }

  const hasChanges =
    targetPlan !== (company.plan ?? "DECOUVERTE") ||
    targetStatus !== (company.status ?? "TRIAL") ||
    targetExpiresAt !== (company.planExpiresAt ? new Date(company.planExpiresAt).toISOString().slice(0, 10) : "") ||
    targetIncludedSeats !== String(company.includedSeats ?? 1) ||
    targetAdditionalSeats !== String(company.additionalSeats ?? 0) ||
    targetPricePerSeat !== (company.pricePerSeat != null ? String(company.pricePerSeat) : "");

  const isPlanFormValid = planReason.trim().length >= 20 && hasChanges;

  return (
    <div className="space-y-6">
      {/* Current plan overview */}
      <div className="grid grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <CreditCard aria-hidden="true" className="h-4 w-4" />
              Abonnement
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Plan actuel</span>
              <Badge variant="outline">{PLAN_LABELS[company.plan] ?? company.plan ?? "Aucun"}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Statut</span>
              <Badge variant={statusCfg.variant}>{statusCfg.label}</Badge>
            </div>
            {company.planExpiresAt && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Expire le</span>
                <span className={
                  new Date(company.planExpiresAt) < new Date()
                    ? "text-destructive font-medium"
                    : new Date(company.planExpiresAt).getTime() - Date.now() < 7 * 86400000
                      ? "text-warning font-medium"
                      : ""
                }>
                  {new Date(company.planExpiresAt).toLocaleDateString("fr-FR")}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Seats inclus / add.</span>
              <span>{company.includedSeats ?? 1} / {company.additionalSeats ?? 0}</span>
            </div>
            {company.pricePerSeat != null && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Prix par seat</span>
                <span>{company.pricePerSeat} €</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Inscrit le</span>
              <span>{new Date(company.createdAt).toLocaleDateString("fr-FR")}</span>
            </div>

            {isSuperAdmin && canAct && (
              <div className="pt-2 border-t border-border">
                <Button size="sm" className="w-full" onClick={openPlanDialog}>
                  <Shield className="h-3.5 w-3.5 mr-1" />
                  Changer le plan
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp aria-hidden="true" className="h-4 w-4" />
              Revenus générés
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {billing ? (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Services</span>
                  <span className="font-medium">{billing.serviceRevenue.toLocaleString("fr-FR")} €</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Assistance</span>
                  <span className="font-medium">{billing.assistanceRevenue.toLocaleString("fr-FR")} €</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Succès fees</span>
                  <span className="font-medium">{billing.successFeeRevenue.toLocaleString("fr-FR")} €</span>
                </div>
                <div className="flex items-center justify-between border-t border-border pt-2 font-semibold">
                  <span>Total CA</span>
                  <span className="text-success">{Math.round(billing.totalRevenue).toLocaleString("fr-FR")} €</span>
                </div>
              </>
            ) : (
              <div className="text-muted-foreground text-center py-4">
                Aucun revenu enregistré
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* No plan info banner */}
      {(!company.plan || company.plan === "DECOUVERTE") && (
        <Card className="border-warning/30 bg-warning/5">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-warning shrink-0" />
              <div>
                <div className="text-sm font-medium">Compte en essai / freemium</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Ce compte n'a pas de plan payant actif. Utilisez "Changer le plan" pour l'upgrader.
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plan change dialog */}
      <Dialog open={showPlanDialog} onOpenChange={(open) => { if (!open) setShowPlanDialog(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Changer le plan</DialogTitle>
            <DialogDescription>
              Modifiez le plan et le statut de l'entreprise. Cette action sera tracée en audit log.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Plan cible</label>
              <Select value={targetPlan} onValueChange={setTargetPlan}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLAN_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Statut</label>
              <Select value={targetStatus} onValueChange={setTargetStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Expiration du plan</label>
              <Input
                type="date"
                value={targetExpiresAt}
                onChange={(e) => setTargetExpiresAt(e.target.value)}
                min={new Date().toISOString().slice(0, 10)}
              />
              <p className="text-xs text-muted-foreground">
                Laissez vide pour un plan sans expiration
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <label className="text-sm font-medium">Seats inclus</label>
                <Input
                  type="number"
                  min="1"
                  value={targetIncludedSeats}
                  onChange={(e) => setTargetIncludedSeats(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Seats add.</label>
                <Input
                  type="number"
                  min="0"
                  value={targetAdditionalSeats}
                  onChange={(e) => setTargetAdditionalSeats(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Prix/seat (€)</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="—"
                  value={targetPricePerSeat}
                  onChange={(e) => setTargetPricePerSeat(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                Motif <span className="text-muted-foreground font-normal">(min. 20 caractères)</span>
              </label>
              <Textarea
                placeholder="Ex : Cadeau commercial suite démo réussie, passage POC 3 mois…"
                value={planReason}
                onChange={(e) => setPlanReason(e.target.value)}
                rows={3}
              />
              {planReason.length > 0 && planReason.trim().length < 20 && (
                <p className="text-xs text-destructive">
                  {20 - planReason.trim().length} caractères restants
                </p>
              )}
            </div>

            {/* Summary of changes */}
            {hasChanges && (
              <Card className="bg-muted/50">
                <CardContent className="py-3 text-xs space-y-1">
                  <div className="font-medium text-sm mb-1">Récapitulatif des changements</div>
                  {targetPlan !== (company.plan ?? "DECOUVERTE") && (
                    <div>
                      Plan : <span className="text-muted-foreground">{PLAN_LABELS[company.plan] ?? company.plan ?? "Aucun"}</span>
                      {" → "}
                      <span className="font-medium">{PLAN_OPTIONS.find((o) => o.value === targetPlan)?.label}</span>
                    </div>
                  )}
                  {targetStatus !== (company.status ?? "TRIAL") && (
                    <div>
                      Statut : <span className="text-muted-foreground">{STATUS_CONFIG[company.status]?.label ?? company.status}</span>
                      {" → "}
                      <span className="font-medium">{STATUS_OPTIONS.find((o) => o.value === targetStatus)?.label}</span>
                    </div>
                  )}
                  {targetExpiresAt !== (company.planExpiresAt ? new Date(company.planExpiresAt).toISOString().slice(0, 10) : "") && (
                    <div>
                      Expiration : <span className="text-muted-foreground">
                        {company.planExpiresAt ? new Date(company.planExpiresAt).toLocaleDateString("fr-FR") : "Aucune"}
                      </span>
                      {" → "}
                      <span className="font-medium">
                        {targetExpiresAt ? new Date(targetExpiresAt).toLocaleDateString("fr-FR") : "Aucune"}
                      </span>
                    </div>
                  )}
                  {targetIncludedSeats !== String(company.includedSeats ?? 1) && (
                    <div>
                      Seats inclus : <span className="text-muted-foreground">{company.includedSeats ?? 1}</span>
                      {" → "}
                      <span className="font-medium">{targetIncludedSeats}</span>
                    </div>
                  )}
                  {targetAdditionalSeats !== String(company.additionalSeats ?? 0) && (
                    <div>
                      Seats add. : <span className="text-muted-foreground">{company.additionalSeats ?? 0}</span>
                      {" → "}
                      <span className="font-medium">{targetAdditionalSeats}</span>
                    </div>
                  )}
                  {targetPricePerSeat !== (company.pricePerSeat != null ? String(company.pricePerSeat) : "") && (
                    <div>
                      Prix/seat : <span className="text-muted-foreground">{company.pricePerSeat ?? "—"} €</span>
                      {" → "}
                      <span className="font-medium">{targetPricePerSeat || "—"} €</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPlanDialog(false)}>
              Annuler
            </Button>
            <Button
              onClick={handlePlanChange}
              disabled={!isPlanFormValid || updateMutation.isPending}
            >
              {updateMutation.isPending ? "Mise à jour…" : "Appliquer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Stripe Section ─── */}
      <div className="border-t border-border pt-6">
        <h3 className="text-sm font-semibold mb-4 text-muted-foreground uppercase tracking-wide">
          Données Stripe
        </h3>
        <StripeSection
          companyId={company.id}
          stripeCustomerId={company.stripeCustomerId ?? null}
          isSuperAdmin={isSuperAdmin}
          canAct={canAct}
          invoices={(invoicesData?.invoices as any[]) ?? []}
          failedPayments={(failedData?.payments as any[]) ?? []}
          paymentMethods={(methodsData?.methods as any[]) ?? []}
          upcomingInvoice={(upcomingData?.invoice as any) ?? null}
          subscription={(subData?.subscription as any) ?? null}
          isLoading={stripeLoading}
          onRetryInvoice={(invoiceId) =>
            retryMutation.mutate({ companyId: company.id, invoiceId })
          }
          onCancelSubscription={(reason) =>
            cancelSubMutation.mutate({ companyId: company.id, reason })
          }
          onRefundInvoice={(invoiceId, amount, reason) =>
            refundMutation.mutate({ companyId: company.id, invoiceId, amount, reason })
          }
          isRetrying={retryMutation.isPending}
          isCancelling={cancelSubMutation.isPending}
          isRefunding={refundMutation.isPending}
        />
      </div>
    </div>
  );
}

// ─── Activity History Tab ─────────────────────────────────────────────────────

const ACTION_LABELS: Record<string, string> = {
  COMPANY_UPDATE: "Mise à jour entreprise",
  COMPANY_NOTE: "Note interne",
  COMPANY_PLAN_CHANGE: "Changement de plan",
  CREDIT_ADD: "Ajout de crédits",
  CREDIT_REMOVE: "Retrait de crédits",
  FEATURE_TOGGLE: "Activation/désactivation fonctionnalité",
  TENDER_ASSIGN: "Attribution AO",
  TENDER_DECISION: "Décision AO",
  SUPPORT_TICKET_UPDATE: "Mise à jour ticket support",
};

function ActivityHistoryTab({ companyId }: { companyId: string }) {
  const [page, setPage] = useState(1);

  const { data, isLoading } = trpc.admin.audit.list.useQuery(
    { companyId, page, limit: 20 },
    { enabled: !!companyId }
  );

  const logs = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 20);

  function formatBeforeAfter(before: any, after: any) {
    if (!before && !after) return null;
    const changes: string[] = [];
    const b = typeof before === "object" && before ? before : {};
    const a = typeof after === "object" && after ? after : {};
    const keys = new Set([...Object.keys(b), ...Object.keys(a)]);
    const skip = new Set(["id", "createdAt", "updatedAt", "password", "stripeCustomerId", "stripeSubscriptionItemId"]);
    for (const key of keys) {
      if (skip.has(key)) continue;
      if (JSON.stringify(b[key]) !== JSON.stringify(a[key])) {
        changes.push(`${key}: ${b[key] ?? "—"} → ${a[key] ?? "—"}`);
      }
    }
    return changes.length > 0 ? changes : null;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
          <History className="h-4 w-4" />
          Historique d'activité ({total})
        </h3>
      </div>

      {isLoading ? (
        <div className="text-center text-muted-foreground text-sm py-8">Chargement…</div>
      ) : logs.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground text-sm">
            Aucune activité enregistrée pour cette entreprise
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[140px]">Date</TableHead>
                    <TableHead className="w-[180px]">Action</TableHead>
                    <TableHead className="w-[140px]">Utilisateur</TableHead>
                    <TableHead>Détails</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log: any) => {
                    const changes = formatBeforeAfter(log.beforeJson, log.afterJson);
                    const actor = log.User
                      ? `${log.User.firstName || ""} ${log.User.lastName || ""}`.trim() || log.User.email
                      : "—";
                    return (
                      <TableRow key={log.id}>
                        <TableCell className="text-xs text-muted-foreground">
                          {new Date(log.createdAt).toLocaleDateString("fr-FR", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {ACTION_LABELS[log.action] ?? log.action}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{actor}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {changes ? (
                            <div className="space-y-0.5 max-w-[300px]">
                              {changes.slice(0, 5).map((c, i) => (
                                <div key={i} className="truncate">{c}</div>
                              ))}
                              {changes.length > 5 && (
                                <div className="text-muted-foreground/70">+{changes.length - 5} autres</div>
                              )}
                            </div>
                          ) : log.afterJson?.message ? (
                            <span className="truncate block max-w-[300px]">{log.afterJson.message}</span>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                ← Précédent
              </Button>
              <span className="text-xs text-muted-foreground">
                Page {page} / {totalPages}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Suivant →
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  sub,
  icon: Icon,
  iconColor = "text-primary",
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon: any;
  iconColor?: string;
}) {
  return (
    <Card>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs text-muted-foreground mb-1">{label}</div>
            <div className="text-2xl font-bold">{value}</div>
            {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
          </div>
          <div aria-hidden="true" className={`p-2 rounded-lg bg-muted ${iconColor}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

// ─── Rapport hebdo (RGPD / support — AOP-8531) ──────────────────────────────────
const WEEKLY_REPORT_DAYS = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];

type WeeklyReportSite = {
  id: string;
  title: string;
  companyId: string;
  weeklyReportEnabled: boolean;
  weeklyReportEmail: string | null;
  weeklyReportDay: number;
  weeklyReportLastSent: string | Date | null;
};

function WeeklyReportTab({ companyId, canAct }: { companyId: string; canAct: boolean }) {
  const [confirmDisable, setConfirmDisable] = useState<WeeklyReportSite | null>(null);

  const { data: sites, isLoading, refetch } = trpc.admin.companies.getCompanySites.useQuery(
    { companyId },
    { enabled: !!companyId }
  );

  const setMutation = trpc.admin.companies.setSiteWeeklyReport.useMutation({
    onSuccess: (data: { title: string; weeklyReportEnabled: boolean }) => {
      toast.success(
        `Rapport hebdo ${data.weeklyReportEnabled ? "activé" : "désactivé"} — ${data.title}`
      );
      setConfirmDisable(null);
      refetch();
    },
    onError: () => toast.error("Erreur lors du changement"),
  });

  function handleToggle(site: WeeklyReportSite) {
    if (!canAct) return;
    if (site.weeklyReportEnabled) {
      // Désactivation = action RGPD/support → confirmation
      setConfirmDisable(site);
    } else {
      setMutation.mutate({ siteId: site.id, enabled: true });
    }
  }

  if (isLoading) {
    return <div className="py-8 text-center text-muted-foreground text-sm">Chargement des chantiers…</div>;
  }

  const list = (sites ?? []) as WeeklyReportSite[];

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
          Rapport hebdo client par chantier
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Activer / désactiver l'envoi du rapport hebdomadaire au client (support RGPD :
          désinscription sur demande du client ou du pro).
        </p>
      </div>

      {list.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground text-sm">
            Aucun chantier pour cette entreprise.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {list.map((site) => (
                <div key={site.id} className="flex items-center justify-between gap-4 px-4 py-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{site.title || "Chantier sans titre"}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <Badge
                        variant="outline"
                        className={
                          site.weeklyReportEnabled
                            ? "bg-success/10 border-success/30 text-success"
                            : "bg-muted/50 text-muted-foreground"
                        }
                      >
                        {site.weeklyReportEnabled ? "Activé" : "Désactivé"}
                      </Badge>
                      <span className="inline-flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {site.weeklyReportEmail || "aucun email destinataire"}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {WEEKLY_REPORT_DAYS[site.weeklyReportDay] ?? `Jour ${site.weeklyReportDay}`}
                      </span>
                      {site.weeklyReportLastSent && (
                        <span>
                          Dernier envoi : {new Date(site.weeklyReportLastSent).toLocaleDateString("fr-FR")}
                        </span>
                      )}
                    </div>
                  </div>
                  <Switch
                    checked={site.weeklyReportEnabled}
                    onCheckedChange={() => handleToggle(site)}
                    disabled={!canAct || setMutation.isPending}
                    aria-label={`Activer/désactiver le rapport hebdo — ${site.title}`}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Confirmation désactivation (RGPD/support) */}
      <Dialog open={!!confirmDisable} onOpenChange={(open) => !open && setConfirmDisable(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Désactiver le rapport hebdo ?</DialogTitle>
            <DialogDescription>
              Le client ne recevra plus l'email hebdomadaire pour le chantier «&nbsp;
              {confirmDisable?.title}&nbsp;»
              {confirmDisable?.weeklyReportEmail ? ` (${confirmDisable.weeklyReportEmail})` : ""}.
              Cette action est tracée (audit RGPD).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDisable(null)} disabled={setMutation.isPending}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={() => confirmDisable && setMutation.mutate({ siteId: confirmDisable.id, enabled: false })}
              disabled={setMutation.isPending}
            >
              {setMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Désactiver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function CompanyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canAct = canWrite(user?.staffRole);

  const [activeTab, setActiveTab] = useState("summary");
  const [suspendDialog, setSuspendDialog] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");

  const { data: company, isLoading } = trpc.admin.companies.getById.useQuery(
    { id: id! },
    { enabled: !!id }
  );

  const companyUtils = trpc.useUtils();

  const isSuperAdmin = user?.staffRole === "SUPER_ADMIN";

  const impersonateMutation = trpc.admin.companies.impersonate.useMutation({
    onSuccess: (data) => {
      // VITE_CLIENT_URL pointe vers l'app SaaS Client (port 8090 en dev, app.constrigo.fr en prod).
      // Le regex `:\d+$` ne match aucun port en prod → fallback sur l'env var.
      const devFallback = window.location.origin.replace(/:\d+$/, ":8090");
      const clientUrl = (import.meta.env.VITE_CLIENT_URL as string | undefined)?.replace(/\/$/, "") || devFallback;
      window.open(`${clientUrl}?impersonate=${data.token}`, "_blank");
      toast.success(`Impersonation de ${(data.company as any)?.name || "l'entreprise"} ouverte dans un nouvel onglet`);
    },
    onError: (err) => toast.error(err.message || "Erreur lors de l'impersonation"),
  });

  const suspendMutation = trpc.admin.travauxbtp.suspendArtisan.useMutation({
    onSuccess: () => {
      toast.success("Artisan suspendu");
      setSuspendDialog(false);
      setSuspendReason("");
      companyUtils.admin.companies.getById.invalidate({ id: id! });
    },
    onError: (err) => toast.error(err.message || "Erreur suspension"),
  });

  const unsuspendMutation = trpc.admin.travauxbtp.unsuspendArtisan.useMutation({
    onSuccess: () => {
      toast.success("Suspension levée");
      companyUtils.admin.companies.getById.invalidate({ id: id! });
    },
    onError: (err) => toast.error(err.message || "Erreur levée suspension"),
  });

  // ── Suppression d'entreprise (AOP-8995.D → admin.companies.delete) ──────────
  // Double confirmation : raison (≥20) + nom exact retapé. Soft par défaut ;
  // hard (purge cascade) derrière un garde fort supplémentaire (PURGE_DEFINITIVE).
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [deleteMode, setDeleteMode] = useState<"soft" | "hard">("soft");
  const [deleteHardConfirm, setDeleteHardConfirm] = useState("");

  const deleteMutation = trpc.admin.companies.delete.useMutation({
    onSuccess: (res) => {
      toast.success(
        res.mode === "hard"
          ? `Entreprise « ${res.name} » purgée définitivement`
          : `Entreprise « ${res.name} » supprimée (soft delete)`
      );
      setDeleteDialog(false);
      navigate("/companies");
    },
    onError: (err) => toast.error(err.message || "Erreur lors de la suppression"),
  });

  function openDeleteDialog() {
    setDeleteReason("");
    setDeleteConfirmName("");
    setDeleteMode("soft");
    setDeleteHardConfirm("");
    setDeleteDialog(true);
  }

  const companyName = (company as any)?.name ?? "";
  const isDeleteFormValid =
    deleteReason.trim().length >= 20 &&
    deleteConfirmName.trim() === companyName.trim() &&
    (deleteMode === "soft" || deleteHardConfirm === "PURGE_DEFINITIVE");

  function handleDelete() {
    if (!id || !isDeleteFormValid) return;
    deleteMutation.mutate({
      companyId: id,
      reason: deleteReason.trim(),
      confirmName: deleteConfirmName.trim(),
      mode: deleteMode,
      ...(deleteMode === "hard" ? { hardConfirm: deleteHardConfirm } : {}),
    });
  }

  if (isLoading) {
    return (
      <AdminLayout>
        <div role="status" className="flex items-center justify-center py-20 text-muted-foreground text-sm">
          Chargement…
        </div>
      </AdminLayout>
    );
  }

  if (!company) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20 text-muted-foreground text-sm">
          Entreprise introuvable
        </div>
      </AdminLayout>
    );
  }

  const statusCfg = STATUS_CONFIG[(company as any).status] ?? STATUS_CONFIG.ACTIVE;
  const kpis = (company as any).kpis;
  const billing = (company as any).billing;
  const projectStats = (company as any).projectStats;
  const tenderMatches: any[] = (company as any).tenderMatches ?? [];
  const supportTickets: any[] = (company as any).supportTickets ?? [];
  const openTickets = supportTickets.filter((t) => TICKET_STATUS_CONFIG[t.status]?.isOpen);
  const closedTickets = supportTickets.filter((t) => !TICKET_STATUS_CONFIG[t.status]?.isOpen);

  // Product detection from company data
  const travauxbtpActive =
    (company as any).travauxbtpDescription !== null ||
    (company as any).leadCredits > 0 ||
    ((company as any)._count?.PrivateProjectMatch || 0) > 0;
  const saasActive = (company as any).plan !== null && (company as any).plan !== "DECOUVERTE";
  const mobileActive = ((company as any).User || []).some((u: any) => u.pushToken);

  const artisanUser = ((company as any).User || []).find((u: any) => u.ArtisanProfile);
  const artisanProfile = artisanUser?.ArtisanProfile;
  const isSuspended = artisanProfile?.pendingVerification === true && artisanProfile?.availability === "NOT_TAKING_NEW_PROJECTS";

  return (
    <AdminLayout>
      {/* Back + header */}
      <div className="flex items-start gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/companies")}>
          <ArrowLeft aria-hidden="true" className="h-4 w-4 mr-1" />
          Entreprises
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold truncate">{(company as any).name}</h1>
            <Badge variant={statusCfg.variant}>{statusCfg.label}</Badge>
            {(company as any).plan && (
              <Badge variant="outline">{PLAN_LABELS[(company as any).plan] ?? (company as any).plan}</Badge>
            )}
            {travauxbtpActive && (
              <Badge className="bg-orange-500/10 text-orange-600 border-orange-200">
                <HardHat className="h-3 w-3 mr-1" />
                TravauxBTP
              </Badge>
            )}
            {saasActive && (
              <Badge className="bg-blue-500/10 text-blue-600 border-blue-200">
                <Layers className="h-3 w-3 mr-1" />
                SaaS
              </Badge>
            )}
            {mobileActive && (
              <Badge className="bg-purple-500/10 text-purple-600 border-purple-200">
                <Smartphone className="h-3 w-3 mr-1" />
                Mobile
              </Badge>
            )}
            {!travauxbtpActive && !saasActive && !mobileActive && (
              <Badge variant="outline">Freemium</Badge>
            )}
          </div>
          <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground flex-wrap">
            {(company as any).email && (
              <span className="flex items-center gap-1">
                <Mail aria-hidden="true" className="h-3.5 w-3.5" />
                {(company as any).email}
              </span>
            )}
            {(company as any).phone && (
              <span className="flex items-center gap-1">
                <Phone aria-hidden="true" className="h-3.5 w-3.5" />
                {(company as any).phone}
              </span>
            )}
            {(company as any).siret && (
              <span className="flex items-center gap-1">
                <Building2 aria-hidden="true" className="h-3.5 w-3.5" />
                SIRET: {(company as any).siret}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Calendar aria-hidden="true" className="h-3.5 w-3.5" />
              Inscrit le {new Date((company as any).createdAt).toLocaleDateString("fr-FR")}
            </span>
          </div>
        </div>

        {/* Quick actions — SUPER_ADMIN only */}
        {canAct && (
          <div className="flex gap-2 shrink-0">
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/messages?company=${id}`)}
            >
              <MessageSquare aria-hidden="true" className="h-3.5 w-3.5 mr-1" />
              Message
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                navigate(
                  `/sales/leads?newLead=1&company=${encodeURIComponent((company as any).name)}&email=${encodeURIComponent((company as any).email ?? "")}`
                )
              }
            >
              <UserPlus aria-hidden="true" className="h-3.5 w-3.5 mr-1" />
              Créer lead
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.open(`/admin/companies/${id}`, "_blank")}
            >
              <ExternalLink aria-hidden="true" className="h-3.5 w-3.5 mr-1" />
              Admin
            </Button>
            {isSuperAdmin && (
              <Button
                size="sm"
                variant="outline"
                className="border-amber-500/50 text-amber-600 hover:bg-amber-50"
                disabled={impersonateMutation.isPending}
                onClick={() => {
                  if (id) impersonateMutation.mutate({ companyId: id });
                }}
              >
                <LogIn aria-hidden="true" className="h-3.5 w-3.5 mr-1" />
                {impersonateMutation.isPending ? "Connexion…" : "Impersonate"}
              </Button>
            )}
            {isSuperAdmin && (
              <Button
                size="sm"
                variant="outline"
                className="border-destructive/50 text-destructive hover:bg-destructive/10"
                onClick={openDeleteDialog}
              >
                <Trash2 aria-hidden="true" className="h-3.5 w-3.5 mr-1" />
                Supprimer
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Suppression d'entreprise — SUPER_ADMIN, double confirmation (AOP-8995.D) */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Supprimer l'entreprise
            </DialogTitle>
            <DialogDescription>
              Action sensible et tracée en audit log. Le mode <strong>soft</strong> retire
              le compte des listes en conservant les données ; le mode <strong>hard</strong>{" "}
              purge définitivement toutes les données (irréversible, comptes de test
              uniquement).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">Mode de suppression</label>
              <Select value={deleteMode} onValueChange={(v) => setDeleteMode(v as "soft" | "hard")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="soft">Soft — masquer (réversible, données conservées)</SelectItem>
                  <SelectItem value="hard">Hard — purge cascade définitive (irréversible)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                Raison <span className="text-muted-foreground">(min. 20 caractères)</span>
              </label>
              <Textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="Ex : compte de test créé par erreur lors de la démo du 27/06…"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                Confirmation — retapez le nom exact :{" "}
                <span className="font-mono text-foreground">{companyName}</span>
              </label>
              <Input
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                placeholder={companyName}
                autoComplete="off"
              />
            </div>

            {deleteMode === "hard" && (
              <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
                <label className="text-sm font-medium text-destructive">
                  Garde fort — tapez <span className="font-mono">PURGE_DEFINITIVE</span> pour confirmer
                  la purge cascade
                </label>
                <Input
                  value={deleteHardConfirm}
                  onChange={(e) => setDeleteHardConfirm(e.target.value)}
                  placeholder="PURGE_DEFINITIVE"
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground">
                  La purge est refusée si le compte porte de la facturation réelle payée.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(false)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              disabled={!isDeleteFormValid || deleteMutation.isPending}
              onClick={handleDelete}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-1" />
              )}
              {deleteMode === "hard" ? "Purger définitivement" : "Supprimer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modération artisan — visible only if company has an ArtisanProfile */}
      {artisanProfile && canAct && (
        <Card className={`mb-6 ${isSuspended ? "border-destructive/40 bg-destructive/5" : "border-muted"}`}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {isSuspended ? (
                  <ShieldAlert className="h-5 w-5 text-destructive shrink-0" />
                ) : (
                  <ShieldCheck className="h-5 w-5 text-green-600 shrink-0" />
                )}
                <div>
                  <div className="text-sm font-medium">
                    {isSuspended ? "Artisan suspendu" : "Artisan actif"}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {isSuspended
                      ? "Ce profil artisan est suspendu — il ne reçoit plus de leads ni n'apparaît dans les résultats"
                      : "Ce profil artisan est en règle et visible sur la plateforme"}
                  </div>
                </div>
              </div>
              <div className="shrink-0">
                {isSuspended ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={unsuspendMutation.isPending}
                    onClick={() =>
                      unsuspendMutation.mutate({
                        artisanProfileId: artisanProfile.id,
                        reason: "Levée de suspension depuis CompanyDetail OPS",
                      })
                    }
                  >
                    {unsuspendMutation.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    ) : (
                      <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                    )}
                    Lever la suspension
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => setSuspendDialog(true)}
                  >
                    <ShieldAlert className="h-3.5 w-3.5 mr-1" />
                    Suspendre l'artisan
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Suspend artisan dialog */}
      <Dialog open={suspendDialog} onOpenChange={(open) => { if (!open) { setSuspendDialog(false); setSuspendReason(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-destructive" />
              Suspendre l'artisan
            </DialogTitle>
            <DialogDescription>
              {(company as any).name} — cette action masque le profil et bloque la réception de leads
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">Raison de la suspension</label>
            <Textarea
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Expliquez la raison de la suspension (min. 10 caractères)..."
              rows={3}
            />
            {suspendReason.length > 0 && suspendReason.length < 10 && (
              <p className="text-xs text-destructive">Minimum 10 caractères requis</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendDialog(false)}>Annuler</Button>
            <Button
              variant="destructive"
              disabled={suspendReason.length < 10 || suspendMutation.isPending}
              onClick={() => {
                if (artisanProfile) {
                  suspendMutation.mutate({
                    artisanProfileId: artisanProfile.id,
                    reason: suspendReason,
                  });
                }
              }}
            >
              {suspendMutation.isPending ? "Suspension…" : "Confirmer la suspension"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6 flex-wrap">
          <TabsTrigger value="summary">Résumé</TabsTrigger>
          <TabsTrigger value="tenders">AO & Projets</TabsTrigger>
          <TabsTrigger value="support">
            Support
            {openTickets.length > 0 && (
              <Badge variant="destructive" className="ml-1.5 h-4 min-w-4 px-1 text-xs">
                {openTickets.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="billing">
            <CreditCard className="h-3.5 w-3.5 mr-1" />
            Facturation
          </TabsTrigger>
          <TabsTrigger value="credits" className="flex items-center gap-1.5">
            <Coins className="h-3.5 w-3.5" />
            Crédits
          </TabsTrigger>
          <TabsTrigger value="features" className="flex items-center gap-1.5">
            <Zap className="h-3.5 w-3.5" />
            Fonctionnalités
          </TabsTrigger>
          <TabsTrigger value="weekly-report" className="flex items-center gap-1.5">
            <Mail className="h-3.5 w-3.5" />
            Rapport hebdo
          </TabsTrigger>
          <TabsTrigger value="access" className="flex items-center gap-1.5">
            <Key className="h-3.5 w-3.5" />
            Accès & Utilisateurs
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-1.5">
            <History className="h-3.5 w-3.5" />
            Historique
          </TabsTrigger>
        </TabsList>

        {/* ─── Résumé ─── */}
        <TabsContent value="summary">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KpiCard
              label="AO répondus"
              value={kpis.tendersAnswered}
              sub={`${projectStats.won} gagnés / ${projectStats.lost} perdus`}
              icon={FileText}
              iconColor="text-primary"
            />
            <KpiCard
              label="Complétude profil"
              value={`${kpis.profileCompletionScore}%`}
              sub="Profil, catégories, zones, logo"
              icon={Target}
              iconColor={kpis.profileCompletionScore >= 75 ? "text-success" : "text-warning"}
            />
            <KpiCard
              label="Dernière connexion"
              value={
                kpis.lastConnection
                  ? new Date(kpis.lastConnection).toLocaleDateString("fr-FR")
                  : "—"
              }
              sub={
                kpis.lastConnection
                  ? `${Math.floor((Date.now() - new Date(kpis.lastConnection).getTime()) / 86400000)}j`
                  : undefined
              }
              icon={Clock}
              iconColor="text-muted-foreground"
            />
            <KpiCard
              label="CA total généré"
              value={`${Math.round(kpis.totalRevenue).toLocaleString("fr-FR")} €`}
              sub="Services + Assistance + Succès"
              icon={Euro}
              iconColor="text-success"
            />
          </div>

          {/* Company info card */}
          <div className="grid grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Informations générales</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {(company as any).address && (
                  <div className="text-muted-foreground">{(company as any).address}</div>
                )}
                <div className="flex items-center gap-2">
                  <Users aria-hidden="true" className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span>{(company as any)._count?.Quote ?? 0} devis créés</span>
                </div>
                <div className="flex items-center gap-2">
                  <BarChart3 aria-hidden="true" className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span>
                    Taux de réussite AO: {projectStats.winRate}% ({projectStats.won}/{projectStats.won + projectStats.lost})
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Tickets support ouverts</CardTitle>
              </CardHeader>
              <CardContent>
                {openTickets.length === 0 ? (
                  <div className="flex items-center gap-2 text-sm text-success">
                    <CheckCircle2 aria-hidden="true" className="h-4 w-4" />
                    Aucun ticket ouvert
                  </div>
                ) : (
                  <div className="space-y-2">
                    {openTickets.slice(0, 3).map((t) => (
                      <div key={t.id} className="flex items-center justify-between text-sm">
                        <span className="truncate text-destructive font-medium">{t.subject}</span>
                        <Badge variant="destructive" className="text-xs ml-2 shrink-0">
                          {TICKET_STATUS_CONFIG[t.status]?.label ?? t.status}
                        </Badge>
                      </div>
                    ))}
                    {openTickets.length > 3 && (
                      <button
                        className="text-xs text-primary underline"
                        onClick={() => setActiveTab("support")}
                      >
                        +{openTickets.length - 3} autres
                      </button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ─── AO & Projets ─── */}
        <TabsContent value="tenders">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileText aria-hidden="true" className="h-4 w-4" />
                Appels d'offres matchés ({tenderMatches.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {tenderMatches.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">Aucun AO matché</p>
              ) : (
                <div className="space-y-2">
                  {tenderMatches.map((tm) => (
                    <div
                      key={tm.id}
                      className="flex items-center justify-between py-2 border-b border-border/50 last:border-0 gap-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{tm.Tender?.title ?? "—"}</div>
                        <div className="text-xs text-muted-foreground">
                          {tm.Tender?.deadlineAt
                            ? `Échéance: ${new Date(tm.Tender.deadlineAt).toLocaleDateString("fr-FR")}`
                            : "Pas d'échéance"}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {tm.decision && (
                          <Badge
                            variant={tm.decision === "GO" ? "default" : tm.decision === "NO_GO" ? "destructive" : "outline"}
                            className="text-xs"
                          >
                            {TENDER_DECISION_LABELS[tm.decision] ?? tm.decision}
                          </Badge>
                        )}
                        {tm.outcomeStatus && (
                          <Badge
                            variant={tm.outcomeStatus === "WON" ? "default" : tm.outcomeStatus === "LOST" ? "destructive" : "secondary"}
                            className="text-xs"
                          >
                            {TENDER_OUTCOME_LABELS[tm.outcomeStatus] ?? tm.outcomeStatus}
                          </Badge>
                        )}
                        {tm.score !== null && tm.score !== undefined && (
                          <span className="text-xs text-muted-foreground">Score: {tm.score}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Support ─── */}
        <TabsContent value="support">
          <div className="space-y-4">
            {openTickets.length > 0 && (
              <Card className="border-destructive/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2 text-destructive">
                    <AlertTriangle aria-hidden="true" className="h-4 w-4" />
                    Tickets ouverts ({openTickets.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {openTickets.map((t) => (
                      <div key={t.id} className="flex items-center justify-between text-sm py-1.5 border-b border-border/50 last:border-0">
                        <div>
                          <div className="font-medium text-destructive">{t.subject}</div>
                          <div className="text-xs text-muted-foreground">
                            Créé le {new Date(t.createdAt).toLocaleDateString("fr-FR")}
                          </div>
                        </div>
                        <Badge variant="destructive" className="text-xs">
                          {TICKET_STATUS_CONFIG[t.status]?.label ?? t.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {closedTickets.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <CheckCircle2 aria-hidden="true" className="h-4 w-4 text-success" />
                    Tickets résolus ({closedTickets.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {closedTickets.map((t) => (
                      <div key={t.id} className="flex items-center justify-between text-sm py-1.5 border-b border-border/50 last:border-0">
                        <div>
                          <div className="font-medium">{t.subject}</div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(t.createdAt).toLocaleDateString("fr-FR")}
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {TICKET_STATUS_CONFIG[t.status]?.label ?? t.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {supportTickets.length === 0 && (
              <Card>
                <CardContent className="py-10 text-center text-muted-foreground text-sm">
                  Aucun ticket support
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* ─── Facturation ─── */}
        <TabsContent value="billing">
          <BillingTab
            company={company as any}
            billing={billing}
            statusCfg={statusCfg}
            canAct={canAct}
            userRole={user?.staffRole}
          />
        </TabsContent>

        <TabsContent value="credits">
          <CreditsTab
            companyId={id!}
            leadCredits={(company as any).leadCredits ?? 0}
            canAct={canAct}
            autoTopupEnabled={(company as any).autoTopupEnabled ?? false}
            autoTopupThreshold={(company as any).autoTopupThreshold ?? null}
            autoTopupPackId={(company as any).autoTopupPackId ?? null}
          />
        </TabsContent>

        <TabsContent value="features">
          <FeaturesTab companyId={id!} canAct={canAct} />
        </TabsContent>

        <TabsContent value="weekly-report">
          <WeeklyReportTab companyId={id!} canAct={canAct} />
        </TabsContent>

        <TabsContent value="access">
          <AccessTab companyId={id!} canAct={canAct} />
        </TabsContent>

        <TabsContent value="history">
          <ActivityHistoryTab companyId={id!} />
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
}
